import React, { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { "X-Admin-Token": t } });
      setBookings(res.data);
      setAuthed(true);
    } catch (e) {
      setError("Invalid access token.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/bookings/${id}`, { status }, { headers: { "X-Admin-Token": token } });
      setBookings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
    } catch (e) {
      console.error(e);
    }
  };

  const updateNotes = async (id, notes) => {
    const res = await axios.patch(`${API}/bookings/${id}`, { notes }, { headers: { "X-Admin-Token": token } });
    setBookings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
  };

  const counts = bookings.reduce(
    (acc, b) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc; },
    { New: 0, Contacted: 0, Quoted: 0 }
  );

  const visible = bookings
    .filter((b) => {
      const matchStatus = filter === "All" || b.status === filter;
      const q = query.trim().toLowerCase();
      const matchQuery =
        !q ||
        [b.name, b.email, b.phone, b.address].some((v) => (v || "").toLowerCase().includes(q));
      return matchStatus && matchQuery;
    })
    .sort((a, b) => {
      if (sortBy === "date") {
        const da = Date.parse(a.preferred_date);
        const db = Date.parse(b.preferred_date);
        return (isNaN(da) ? Infinity : da) - (isNaN(db) ? Infinity : db);
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const exportCSV = () => {
    const cols = ["name", "email", "phone", "address", "frequency", "home_size", "bedrooms", "bathrooms", "has_pets", "preferred_date", "focus_areas", "status", "notes", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = visible.map((b) =>
      cols.map((c) => esc(Array.isArray(b[c]) ? b[c].join("; ") : b[c])).join(",")
    );
    const csv = [cols.join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `prestige-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-warmwhite flex items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-sm">
          <p className="overline text-champagne mb-5">The Standard</p>
          <h1 className="font-serif text-4xl font-light text-charcoal mb-8">Concierge Dashboard</h1>
          <label className="block text-charcoal/60 text-sm mb-2">Access Token</label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(token)}
            className="w-full px-5 py-4 border border-stone/70 rounded-sm bg-warmwhite text-charcoal focus:outline-none focus:border-champagne"
            data-testid="admin-token-input"
          />
          {error && <p className="text-red-600 text-sm mt-3" data-testid="admin-error">{error}</p>}
          <button
            onClick={() => load(token)}
            disabled={loading}
            className="mt-6 w-full py-4 bg-charcoal text-warmwhite rounded-full text-sm font-semibold transition-all hover:bg-champagne disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? "Verifying…" : "Enter"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warmwhite px-6 md:px-12 py-16" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1400px]">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline text-champagne mb-4">Concierge Dashboard</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light text-charcoal">Cleaning Plan Requests</h1>
          </div>
          <span className="text-charcoal/50 text-sm">{bookings.length} total</span>
        </div>

        {/* Filter + search bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10 pb-6 border-b border-stone/60">
          <div className="flex flex-wrap gap-2" data-testid="filter-bar">
            {["All", "New", "Contacted", "Quoted"].map((s) => {
              const n = s === "All" ? bookings.length : counts[s];
              return (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                    filter === s ? "bg-charcoal text-warmwhite" : "border border-stone/70 text-charcoal/70 hover:border-charcoal"
                  }`}
                  data-testid={`filter-${s}`}
                >
                  {s} <span className="opacity-60">({n})</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full sm:w-auto px-4 py-3 border border-stone/70 rounded-full bg-warmwhite text-charcoal text-sm focus:outline-none focus:border-champagne cursor-pointer"
              data-testid="admin-sort"
            >
              <option value="newest">Newest first</option>
              <option value="date">Preferred date (soonest)</option>
            </select>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, address…"
              className="w-full sm:w-64 px-5 py-3 border border-stone/70 rounded-full bg-warmwhite text-charcoal text-sm focus:outline-none focus:border-champagne"
              data-testid="admin-search"
            />
            <button
              onClick={exportCSV}
              disabled={visible.length === 0}
              className="w-full sm:w-auto shrink-0 px-5 py-3 rounded-full text-sm font-semibold bg-charcoal text-warmwhite transition-colors hover:bg-champagne disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-export-csv"
            >
              Export CSV
            </button>
          </div>
        </div>

        {bookings.length === 0 ? (
          <p className="text-charcoal/50" data-testid="admin-empty">No requests yet.</p>
        ) : visible.length === 0 ? (
          <p className="text-charcoal/50" data-testid="admin-no-results">No requests match your filters.</p>
        ) : (
          <div className="space-y-4">
            {visible.map((b) => (
              <div key={b.id} className="border border-stone/70 p-8 bg-warmwhite" data-testid={`booking-row-${b.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-serif text-2xl text-charcoal">{b.name}</h3>
                      <StatusBadge status={b.status} />
                    </div>
                    <p className="text-charcoal/50 text-sm mt-1">{b.email} · {b.phone}</p>
                    <p className="text-charcoal/50 text-sm">{b.address}</p>
                  </div>
                  <span className="text-xs text-charcoal/40">{new Date(b.created_at).toLocaleString()}</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <Field label="Frequency" value={b.frequency} />
                  <Field label="Home Size" value={b.home_size} />
                  <Field label="Bedrooms" value={b.bedrooms} />
                  <Field label="Bathrooms" value={b.bathrooms} />
                  <Field label="Pets" value={b.has_pets} />
                  <Field label="Preferred Date" value={b.preferred_date} />
                  <div className="sm:col-span-2">
                    <p className="text-charcoal/40 text-xs uppercase tracking-wider mb-1">Focus Areas</p>
                    <p className="text-charcoal">{b.focus_areas?.length ? b.focus_areas.join(", ") : "—"}</p>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-stone/60 flex flex-wrap items-center gap-2">
                  <span className="text-charcoal/40 text-xs uppercase tracking-wider mr-2">Mark as</span>
                  {["New", "Contacted", "Quoted"].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(b.id, s)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${
                        b.status === s ? "bg-charcoal text-warmwhite" : "border border-stone/70 text-charcoal/70 hover:border-charcoal"
                      }`}
                      data-testid={`status-btn-${b.id}-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                <NotesEditor booking={b} onSave={updateNotes} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-charcoal/40 text-xs uppercase tracking-wider mb-1">{label}</p>
    <p className="text-charcoal">{value || "—"}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    New: "bg-champagne/20 text-champagne",
    Contacted: "bg-forest/15 text-forest",
    Quoted: "bg-charcoal text-warmwhite",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status] || map.New}`} data-testid="status-badge">
      {status || "New"}
    </span>
  );
};

const NotesEditor = ({ booking, onSave }) => {
  const [value, setValue] = useState(booking.notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = value !== (booking.notes || "");

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(booking.id, value);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-stone/60">
      <div className="flex items-center justify-between mb-2">
        <p className="text-charcoal/40 text-xs uppercase tracking-wider">Private Notes</p>
        {saved && <span className="text-forest text-xs" data-testid={`notes-saved-${booking.id}`}>Saved ✓</span>}
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Jot call details, quote figures, access instructions…"
        rows={3}
        className="w-full px-4 py-3 border border-stone/70 rounded-sm bg-warmwhite text-charcoal text-sm focus:outline-none focus:border-champagne resize-y"
        data-testid={`notes-input-${booking.id}`}
      />
      <button
        onClick={save}
        disabled={!dirty || saving}
        className="mt-3 px-5 py-2 rounded-full text-xs font-semibold bg-charcoal text-warmwhite transition-colors hover:bg-champagne disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={`notes-save-${booking.id}`}
      >
        {saving ? "Saving…" : "Save Note"}
      </button>
    </div>
  );
};
